/* import __COLOCATED_TEMPLATE__ from './proposal.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */

import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { registerDestructor } from '@ember/destroyable';
import { dropTask } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { get, post } from 'embercom/lib/ajax';
import type AppService from 'embercom/services/app-service';
import type IntlService from 'ember-intl/services/intl';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';

/**
 * Entity type constants matching the backend EntityType enum
 */
const EntityType = {
  ArticleContent: 120,
  ContentSnippet: 131,
} as const;

interface ProposalDiffResponse {
  uuid: string;
  status: string;
  entity_type: number;
  entity_type_name: string;
  entity_id: number;
  title: string;
  original_content: Array<Record<string, unknown>>;
  proposed_content: Array<Record<string, unknown>>;
  change_stats: {
    additions?: number;
    deletions?: number;
  };
  version: number;
  created_at: string;
  updated_at: string;
}

interface Args {
  chatId: string | null;
  proposalUuid: string | null;
  entityType: string | null;
  entityId: string | null;
  onClose: () => void;
}

interface Signature {
  Args: Args;
}

/**
 * Component for displaying proposal diffs.
 * Renders inside a ReactWormhole for side-by-side layout with the chat.
 */
export default class Proposal extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;
  @service declare reactTeammateAppIsland: ReactTeammateAppIslandService;

  @tracked proposalData: ProposalDiffResponse | null = null;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.loadProposalTask).perform();

    // Subscribe to proposal tool completion events from React
    let unsubscribe = this.reactTeammateAppIsland.subscribeToTeammateAppEvent(
      'internal-agent:proposal-tool-completed',
      (data: Record<string, unknown>) => {
        this.handleProposalToolCompleted(data);
      },
    );
    registerDestructor(this, unsubscribe);
  }

  get app() {
    return this.appService.app;
  }

  get isLoading() {
    return taskFor(this.loadProposalTask).isRunning;
  }

  get isArticleContent(): boolean {
    return this.proposalData?.entity_type === EntityType.ArticleContent;
  }

  get isContentSnippet(): boolean {
    return this.proposalData?.entity_type === EntityType.ContentSnippet;
  }

  get isPending(): boolean {
    return this.proposalData?.status === 'pending';
  }

  get isApplyRunning(): boolean {
    return taskFor(this.applyProposal).isRunning;
  }

  get isRejectRunning(): boolean {
    return taskFor(this.rejectProposal).isRunning;
  }

  get statusText(): string {
    if (!this.proposalData) {
      return '';
    }

    switch (this.proposalData.status) {
      case 'pending':
        return this.intl.t('internal-agent.proposal.status.pending');
      case 'applied':
        return this.intl.t('internal-agent.proposal.status.applied');
      case 'rejected':
        return this.intl.t('internal-agent.proposal.status.rejected');
      default:
        return this.proposalData.status;
    }
  }

  private handleProposalToolCompleted(data: Record<string, unknown>): void {
    let { chatId, proposalUuid, version } = data as {
      chatId: string;
      proposalUuid: string;
      version: number;
    };

    // Only process if this is the same chat
    if (chatId !== this.args.chatId) {
      return;
    }

    // Only reload if:
    // 1. This is the same proposal we're viewing
    // 2. The new version is higher than what we have
    // 3. Our proposal is still pending (applied/rejected don't need updates)
    if (
      proposalUuid === this.args.proposalUuid &&
      this.proposalData &&
      version > this.proposalData.version &&
      this.proposalData.status === 'pending'
    ) {
      taskFor(this.loadProposalTask).perform();
    }
  }

  @dropTask
  *loadProposalTask() {
    if (!this.args.chatId || !this.args.proposalUuid) {
      return;
    }

    try {
      let url = `/ember/internal-agent/proposals/${this.args.chatId}/${this.args.proposalUuid}/diff`;
      this.proposalData = yield get(url, {
        app_id: this.app.id,
        admin_id: this.app.currentAdmin.id,
      });
    } catch (e: unknown) {
      this.notificationsService.notifyError(this.intl.t('internal-agent.proposal.load-error'));
      this.args.onClose();
    }
  }

  @dropTask
  *applyProposal() {
    if (!this.proposalData || !this.args.chatId || !this.args.proposalUuid) {
      return;
    }

    try {
      yield post(`/ember/internal-agent/proposals/${this.args.chatId}/approve`, {
        app_id: this.app.id,
        entity_type: this.proposalData.entity_type,
        entity_id: this.proposalData.entity_id,
      });

      this.notificationsService.notifyConfirmation(
        this.intl.t('internal-agent.proposal.apply-success'),
      );

      // Notify React about the status change via TeammateAppEvent
      this.reactTeammateAppIsland.dispatchBridgeEvent('teammate-app:event', {
        identifier: 'internal-agent:proposal-status-changed',
        data: {
          chatId: this.args.chatId,
          proposalUuid: this.args.proposalUuid,
          status: 'applied',
        },
      });

      // Reload to show updated status
      yield taskFor(this.loadProposalTask).perform();
    } catch (e: unknown) {
      this.notificationsService.notifyError(this.intl.t('internal-agent.proposal.apply-error'));
    }
  }

  @dropTask
  *rejectProposal() {
    if (!this.proposalData || !this.args.chatId || !this.args.proposalUuid) {
      return;
    }

    try {
      yield post(`/ember/internal-agent/proposals/${this.args.chatId}/reject`, {
        app_id: this.app.id,
        entity_type: this.proposalData.entity_type,
        entity_id: this.proposalData.entity_id,
      });

      this.notificationsService.notifyConfirmation(
        this.intl.t('internal-agent.proposal.reject-success'),
      );

      // Notify React about the status change via TeammateAppEvent
      this.reactTeammateAppIsland.dispatchBridgeEvent('teammate-app:event', {
        identifier: 'internal-agent:proposal-status-changed',
        data: {
          chatId: this.args.chatId,
          proposalUuid: this.args.proposalUuid,
          status: 'rejected',
        },
      });

      // Reload to show updated status
      yield taskFor(this.loadProposalTask).perform();
    } catch (e: unknown) {
      this.notificationsService.notifyError(this.intl.t('internal-agent.proposal.reject-error'));
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'InternalAgent::Content::Proposal': typeof Proposal;
    'internal-agent/content/proposal': typeof Proposal;
  }
}
